import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";

function formatError(err) {
  if (err.response) {
    const { status, data } = err.response;
    if (data?.detail) {
      const d = data.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
      return String(d);
    }
    if (status === 500) return "Server error — please try again or contact support.";
    if (status === 429) return "Too many attempts. Please wait 15 minutes.";
    return `Request failed (${status})`;
  }
  if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
    return "Unable to reach the server. Please check your connection and try again.";
  }
  if (err.message) return err.message;
  return "Connection error — check your network.";
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl text-[#D4AF37] tracking-wider">LUXIFER 6</h1>
          <p className="text-white/30 text-xs tracking-[0.3em] mt-2 uppercase">Admin Panel</p>
        </div>

        {/* Error */}
        {error && (
          <div data-testid="login-error" className="mb-6 p-3 bg-[#8B0000]/10 border border-[#8B0000]/30 text-[#ff6b6b] text-sm rounded text-center">
            {error}
          </div>
        )}

        {/* Google Sign-In — Primary */}
        <Button
          data-testid="google-login-btn"
          onClick={startGoogleLogin}
          className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3.5 h-auto text-sm flex items-center justify-center gap-3 rounded transition-all duration-300 mb-4"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-[1px] bg-white/10" />
          <span className="text-white/20 text-[10px] uppercase tracking-wider">or</span>
          <div className="flex-1 h-[1px] bg-white/10" />
        </div>

        {/* Password toggle */}
        {!showPasswordLogin ? (
          <button
            data-testid="show-password-login"
            onClick={() => setShowPasswordLogin(true)}
            className="w-full text-center text-xs text-white/30 hover:text-white/50 transition-colors uppercase tracking-wider py-2"
          >
            Sign in with email & password
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 block font-semibold">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20 pointer-events-none" />
                <input
                  data-testid="admin-login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@luxifer6.com"
                  required
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] outline-none pl-10 pr-4 py-3 text-sm text-white rounded placeholder:text-white/20 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 block font-semibold">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20 pointer-events-none" />
                <input
                  data-testid="admin-login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] outline-none pl-10 pr-12 py-3 text-sm text-white rounded placeholder:text-white/20 transition-colors"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black hover:bg-white font-semibold py-3 h-auto text-sm uppercase tracking-wider transition-all duration-300"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
            <div className="text-center">
              <Link to="/admin/forgot-password" className="text-xs text-white/25 hover:text-[#D4AF37] transition-colors">Forgot password?</Link>
            </div>
          </form>
        )}

        <div className="mt-10 text-center">
          <Link to="/" className="text-xs text-white/20 hover:text-white/40 transition-colors uppercase tracking-wider">Back to Site</Link>
        </div>
      </div>
    </div>
  );
}
